import dayjs from "dayjs";
import { MetricType } from "../Metrics.types";
import { HydratedMetric, HydratedMetricValues } from "../HydratedMetric";
import { PeriodRange } from "../PeriodRange";
import { HydratedValue } from "../HydratedValue";
import { Period } from "../Period";
import { formatMetricValue } from "../helpers/formatMetricValue";

const interleave = (arr: string[], sep: string) =>
    arr.flatMap((v, i) => (i === 0 ? [v] : [sep, v]));

export type DeltaResult =
    | { value: HydratedValue }
    | { value: HydratedValue; missingPeriods: Period[] }
    | { value: HydratedValue; divisionByZero: true }
    | { value: HydratedValue; invalidWindowSize: true };

export type DeltaFormula = {
    previous: { title: string; formula: string[]; total: string };
    current: { title: string; formula: string[]; total: string };
    delta: { title: string; formula: string[]; total: string };
};

export abstract class DeltaBase {
    protected metric: HydratedMetric;
    protected asOfDate: dayjs.Dayjs;
    private _result: DeltaResult | null = null;

    constructor(metric: HydratedMetric, asOfDate: dayjs.Dayjs) {
        this.metric = metric;
        this.asOfDate = asOfDate;
    }

    get result(): DeltaResult {
        if (!this._result) {
            this._result = this.computeDelta();
        }
        return this._result;
    }

    hasError(): boolean {
        return (
            "missingPeriods" in this.result ||
            "divisionByZero" in this.result ||
            "invalidWindowSize" in this.result
        );
    }

    getErrorMessage(): string | null {
        if ("missingPeriods" in this.result)
            return "Required data is unavailable.";
        if ("divisionByZero" in this.result)
            return "Division by zero is not possible.";
        if ("invalidWindowSize" in this.result)
            return "Required data is unavailable.";
        return null;
    }

    abstract getCurrentSlice(): HydratedMetricValues;
    abstract getPreviousSlice(): HydratedMetricValues;
    abstract getDeltaLabel(): string;
    protected abstract validate(): DeltaResult | null;

    protected getPaddedValuesForPeriods(
        expectedPeriods: Period[],
    ): HydratedMetricValues {
        const valueMap = new Map(
            this.metric.values.map((v) => [
                v.period.base.startOf("day").valueOf(),
                v,
            ]),
        );

        return expectedPeriods.map((period) => {
            const existing = valueMap.get(period.base.startOf("day").valueOf());
            return (
                existing || {
                    period,
                    value: new HydratedValue(null, this.metric.type),
                }
            );
        });
    }

    getMissingPeriods(): Period[] {
        const allValues = [
            ...this.getCurrentSlice(),
            ...this.getPreviousSlice(),
        ];
        return allValues.filter((v) => v.value.isNull()).map((v) => v.period);
    }

    getCurrentSum(): number {
        return this.getCurrentSlice().reduce(
            (sum, v) => sum + v.value.getValueOrZero(),
            0,
        );
    }

    getPreviousSum(): number {
        return this.getPreviousSlice().reduce(
            (sum, v) => sum + v.value.getValueOrZero(),
            0,
        );
    }

    private computeDelta(): DeltaResult {
        const validationError = this.validate();
        if (validationError) return validationError;

        const missingPeriods = this.getMissingPeriods();
        if (missingPeriods.length > 0) {
            return {
                value: new HydratedValue(null, this.metric.type),
                missingPeriods,
            };
        }

        if (this.getPreviousSum() === 0) {
            return {
                value: new HydratedValue(null, this.metric.type),
                divisionByZero: true,
            };
        }

        return {
            value: new HydratedValue(
                (this.getCurrentSum() - this.getPreviousSum()) /
                    Math.abs(this.getPreviousSum()),
                MetricType.PERCENTAGE,
            ),
        };
    }

    getDeltaFormula(): DeltaFormula {
        const formatOrMissing = (v: HydratedMetricValues[number]) =>
            v.value.isNull()
                ? `${v.period.getFullName(this.metric.resolution)} (Missing)`
                : v.value.getFormattedValue();

        const previousSlice = this.getPreviousSlice();
        const currentSlice = this.getCurrentSlice();

        const previousHasMissing = previousSlice.some((v) => v.value.isNull());
        const currentHasMissing = currentSlice.some((v) => v.value.isNull());

        const previous = previousSlice.map(formatOrMissing);
        const current = currentSlice.map(formatOrMissing);

        const previousRange = PeriodRange.fromArray(
            previousSlice.map((v) => v.period),
        );
        const currentRange = PeriodRange.fromArray(
            currentSlice.map((v) => v.period),
        );

        const prevTitle =
            previousRange?.toStringWithFullNames(this.metric.resolution) ?? "-";
        const currentTitle =
            currentRange?.toStringWithFullNames(this.metric.resolution) ?? "-";

        return {
            previous: {
                title: prevTitle,
                formula: interleave(previous, "+"),
                total: previousHasMissing
                    ? "-"
                    : formatMetricValue(
                          this.getPreviousSum(),
                          this.metric.type,
                      ),
            },
            current: {
                title: currentTitle,
                formula: interleave(current, "+"),
                total: currentHasMissing
                    ? "-"
                    : formatMetricValue(this.getCurrentSum(), this.metric.type),
            },
            delta: {
                title: this.getDeltaLabel(),
                formula: [
                    "(",
                    currentTitle,
                    "-",
                    prevTitle,
                    ")",
                    "/",
                    "| " + prevTitle + " |",
                ],
                total: this.result.value.getFormattedValue(),
            },
        };
    }
}
